import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isDemoUser } from "@/lib/demoMode";
import { getTourSteps } from "@/lib/tourConfig";

const WELCOME_SEEN_KEY = "ddh_tour_welcome_seen";

const TourContext = createContext(null);

/**
 * DriveDesk Hub Presentation Mode — Guided Tour state.
 *
 * Deliberately session-scoped (sessionStorage, not localStorage) per
 * product spec: "If dismissed, do not show it again during that browser
 * session" — a fresh browser session (new tab/window) should offer it
 * again. Entirely inert for real (non-demo) users: `steps` is always
 * empty and every action is a no-op, so nothing here can affect a real
 * customer's experience.
 */
export const TourProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const demo = isDemoUser(user);

  const steps = useMemo(() => (demo ? getTourSteps(user?.role) : []), [demo, user?.role]);

  const [showWelcome, setShowWelcome] = useState(false);
  const [stepIndex, setStepIndex] = useState(-1);
  const relaunchRef = useRef(null);

  // Offer the welcome prompt once per browser session, the first time a
  // demo visitor lands on a dashboard page with a non-empty tour.
  useEffect(() => {
    if (!demo || steps.length === 0) return undefined;
    if (sessionStorage.getItem(WELCOME_SEEN_KEY)) return undefined;
    // Mark as "seen" only once the timer actually fires, not when the
    // effect first runs — React 18 StrictMode (dev only) mounts every
    // effect twice in a row, and marking eagerly here would let the
    // very first (immediately-cleaned-up) mount permanently suppress
    // the real one before the delay ever elapses.
    const timer = setTimeout(() => {
      sessionStorage.setItem(WELCOME_SEEN_KEY, "1");
      setShowWelcome(true);
    }, 900);
    return () => clearTimeout(timer);
  }, [demo]);

  const goToStep = useCallback((index) => {
    if (index < 0 || index >= steps.length) {
      setStepIndex(-1);
      // Return focus to the relaunch pill when the tour ends, per the
      // design guidelines' focus-management requirement.
      relaunchRef.current?.focus();
      return;
    }
    const step = steps[index];
    if (step.route && step.route !== location.pathname) {
      navigate(step.route);
    }
    setStepIndex(index);
  }, [steps, location.pathname, navigate]);

  const startTour = useCallback(() => {
    setShowWelcome(false);
    goToStep(0);
  }, [goToStep]);

  const dismissWelcome = useCallback(() => setShowWelcome(false), []);
  const next = useCallback(() => goToStep(stepIndex + 1), [goToStep, stepIndex]);
  const prev = useCallback(() => goToStep(stepIndex - 1), [goToStep, stepIndex]);
  const endTour = useCallback(() => goToStep(-1), [goToStep]);

  const value = useMemo(() => ({
    enabled: demo && steps.length > 0,
    steps,
    stepIndex,
    activeStep: stepIndex >= 0 ? steps[stepIndex] : null,
    showWelcome,
    startTour,
    dismissWelcome,
    next,
    prev,
    skip: endTour,
    finish: endTour,
    relaunchRef,
  }), [demo, steps, stepIndex, showWelcome, startTour, dismissWelcome, next, prev, endTour]);

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export const useTour = () => {
  const ctx = useContext(TourContext);
  if (!ctx) {
    // Safe no-op fallback so any component can call useTour() even if a
    // future page ever renders outside the provider by mistake.
    return {
      enabled: false, steps: [], stepIndex: -1, activeStep: null, showWelcome: false,
      startTour: () => {}, dismissWelcome: () => {}, next: () => {}, prev: () => {},
      skip: () => {}, finish: () => {}, relaunchRef: { current: null },
    };
  }
  return ctx;
};
